import { useCallback, useEffect, useRef, useState } from 'react';

// Voice-activity detection tuning: a segment is cut only once the speaker
// actually pauses, not on a fixed timer — so lines break at natural points.
const SILENCE_RMS_THRESHOLD = 0.02; // amplitude below this counts as silence
const SILENCE_HOLD_MS = 700; // how long silence must persist to count as "a pause"
const MIN_SEGMENT_MS = 1500; // don't cut segments shorter than this
const MAX_SEGMENT_MS = 20000; // hard cap in case speech never pauses
const MONITOR_INTERVAL_MS = 100;

/**
 * Captures microphone and/or tab/system audio, mixes tracks, and hands off
 * self-contained audio segments to `transcribeChunk` for transcription. Segments
 * are cut at detected pauses in speech (rather than a fixed interval) so each
 * transcribed line corresponds to a natural break.
 * Uses the supplied local transcription function for each segment.
 */
export function useAudioCapture({ onTranscript, transcribeChunk }) {
  const [status, setStatus] = useState('idle'); // idle | requesting | recording | error
  const [sourceLabel, setSourceLabel] = useState('');
  const [error, setError] = useState(null);

  const streamsRef = useRef([]);
  const audioContextRef = useRef(null);
  const mixedStreamRef = useRef(null);
  const analyserRef = useRef(null);
  const monitorTimerRef = useRef(null);
  const recorderRef = useRef(null);
  const generationRef = useRef(0);
  const queueRef = useRef(Promise.resolve());

  const silenceStartRef = useRef(null);
  const hasSpeechRef = useRef(false);
  const segmentStartRef = useRef(0);

  const cleanup = useCallback(() => {
    clearInterval(monitorTimerRef.current);
    monitorTimerRef.current = null;
    streamsRef.current.forEach((s) => s.getTracks().forEach((t) => t.stop()));
    streamsRef.current = [];
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    mixedStreamRef.current = null;
    analyserRef.current = null;
  }, []);

  const uploadChunk = useCallback(
    async (blob) => {
      if (!blob || blob.size < 800) return; // ignore near-silent/empty segments
      try {
        const text = await transcribeChunk(blob);
        setError(null);
        if (text?.trim()) await onTranscript?.(text.trim());
      } catch (err) {
        setError(`Transcription failed: ${err.message}`);
      }
    },
    [onTranscript, transcribeChunk]
  );

  const startNewSegment = useCallback(() => {
    const stream = mixedStreamRef.current;
    if (!stream) return;
    const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'].find((type) => MediaRecorder.isTypeSupported(type));
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    const chunks = [];
    recorder.ondataavailable = (event) => {
      if (event.data?.size) chunks.push(event.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: recorder.mimeType });
      queueRef.current = queueRef.current.then(() => uploadChunk(blob));
    };
    recorder.start();
    recorderRef.current = recorder;
    segmentStartRef.current = Date.now();
    silenceStartRef.current = null;
    hasSpeechRef.current = false;
  }, [uploadChunk]);

  const rotateSegment = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
    startNewSegment();
  }, [startNewSegment]);

  const checkForPause = useCallback(() => {
    const analyser = analyserRef.current;
    if (!analyser) return;

    const buffer = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(buffer);
    let sumSquares = 0;
    for (let i = 0; i < buffer.length; i++) {
      const normalized = (buffer[i] - 128) / 128;
      sumSquares += normalized * normalized;
    }
    const rms = Math.sqrt(sumSquares / buffer.length);

    const now = Date.now();
    const segmentAge = now - segmentStartRef.current;

    if (rms > SILENCE_RMS_THRESHOLD) {
      hasSpeechRef.current = true;
      silenceStartRef.current = null;
    } else if (hasSpeechRef.current) {
      if (silenceStartRef.current == null) silenceStartRef.current = now;
      const silenceDuration = now - silenceStartRef.current;
      if (silenceDuration >= SILENCE_HOLD_MS && segmentAge >= MIN_SEGMENT_MS) {
        rotateSegment();
        return;
      }
    }

    if (segmentAge >= MAX_SEGMENT_MS) {
      rotateSegment();
    }
  }, [rotateSegment]);

  const start = useCallback(
    async (mode) => {
      const generation = ++generationRef.current;
      const keepStream = (stream) => {
        if (generation !== generationRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          throw new Error('Recording cancelled.');
        }
        streamsRef.current.push(stream);
        return stream;
      };
      setError(null);
      setStatus('requesting');
      try {
        const streams = streamsRef.current;

        if (mode === 'mic' || mode === 'both') {
          const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
          keepStream(micStream);
        }

        if (mode === 'system' || mode === 'both') {
          // video: true is required by browsers to grant a picker that offers "share audio"
          const displayStream = await navigator.mediaDevices.getDisplayMedia({
            video: true,
            audio: true,
          });
          keepStream(displayStream);
          if (displayStream.getAudioTracks().length === 0) {
            displayStream.getTracks().forEach((t) => t.stop());
            throw new Error(
              'No audio was shared. In the picker, choose a tab/screen and enable "Share tab audio" / "Share system audio".'
            );
          }

        }

        streamsRef.current = streams;

        const audioContext = new AudioContext();
        audioContextRef.current = audioContext;
        const destination = audioContext.createMediaStreamDestination();
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 1024;
        analyserRef.current = analyser;

        streams.forEach((s) => {
          if (s.getAudioTracks().length > 0) {
            const source = audioContext.createMediaStreamSource(s);
            source.connect(destination);
            source.connect(analyser);
          }
        });
        mixedStreamRef.current = destination.stream;

        streams.forEach((stream) => stream.getAudioTracks().forEach((track) => track.addEventListener('ended', () => {
          clearInterval(monitorTimerRef.current);
          if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
          cleanup();
          setStatus('idle');
        }, { once: true })));
        startNewSegment();
        monitorTimerRef.current = setInterval(checkForPause, MONITOR_INTERVAL_MS);

        setSourceLabel(mode);
        setStatus('recording');
      } catch (err) {
        console.error(err);
        setError(err.message || 'Failed to start recording');
        cleanup();
        setStatus('error');
      }
    },
    [cleanup, checkForPause, startNewSegment]
  );

  const stop = useCallback(() => {
    generationRef.current += 1;
    if (monitorTimerRef.current) clearInterval(monitorTimerRef.current);
    monitorTimerRef.current = null;
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
    cleanup();
    setStatus('idle');
    setSourceLabel('');
  }, [cleanup]);

  useEffect(() => () => stop(), [stop]);

  return { start, stop, status, error, sourceLabel };
}
